import React, { useState, useEffect, useMemo, useCallback } from 'react';
import ClientShow from './ClientShow';

const COLORS = {
  surveys_created: '#3b82f6',
  surveys_edited:  '#22c55e',
  tasks_created:   '#f59e0b',
  tasks_edited:    '#ef4444',
};

const FILTERS = [
  { value: 'all',        label: 'Todos',                       icon: 'fa-users' },
  { value: 'yesterday',  label: 'Activos ayer',                icon: 'fa-check-circle',  color: '#22c55e' },
  { value: 'no_yesterday', label: 'Sin actividad ayer',        icon: 'fa-times-circle',  color: '#ef4444' },
  { value: 'week',       label: 'Activos últimos 7 días',      icon: 'fa-calendar-week', color: '#3b82f6' },
  { value: 'no_week',    label: 'Inactivos últimos 7 días',    icon: 'fa-exclamation-triangle', color: '#f59e0b' },
  { value: 'no_month',   label: 'Inactivos últimos 30 días',   icon: 'fa-skull',         color: '#7c3aed' },
];

const SORT_OPTIONS = [
  { value: 'name',          label: 'Nombre' },
  { value: 'last_activity', label: 'Última actividad' },
  { value: 'yesterday',     label: 'Actividad ayer' },
  { value: 'week',          label: 'Actividad 7d' },
  { value: 'month',         label: 'Actividad 30d' },
  { value: 'period',        label: 'Actividad 6m' },
];

export default function ClientsDashboard({ plans }) {
  const [stats, setStats]     = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);

  const [months, setMonths]       = useState(6);
  const [chartPeriod, setChartPeriod] = useState('yesterday');
  const [chartFrom, setChartFrom] = useState('');
  const [chartTo, setChartTo]     = useState('');
  const [filter, setFilter]       = useState('all');
  const [planId, setPlanId]       = useState('');
  const [q, setQ]                 = useState('');
  const [sortBy, setSortBy]       = useState('yesterday');
  const [sortDir, setSortDir]     = useState('DESC');
  const [selectedClientId, setSelectedClientId] = useState(null);

  const fetchStats = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams({ months });
    if (chartFrom && chartTo) {
      params.append('chart_from', chartFrom);
      params.append('chart_to', chartTo);
    } else {
      params.append('chart_period', chartPeriod);
    }
    fetch(`/settings/clients_v2/dashboard_stats?${params}`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    })
      .then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
      .then(j => { setStats(j); setLoading(false); })
      .catch(e => { setError(e.message); setLoading(false); });
  }, [months, chartPeriod, chartFrom, chartTo]);

  const pickPreset = useCallback((v) => {
    setChartPeriod(v);
    setChartFrom('');
    setChartTo('');
  }, []);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  const filteredTenants = useMemo(() => {
    if (!stats?.by_tenant) return [];
    let list = stats.by_tenant;

    if (planId) list = list.filter(t => t.plan?.id === Number(planId));
    if (q) {
      const ql = q.toLowerCase();
      list = list.filter(t => (t.name || '').toLowerCase().includes(ql));
    }

    switch (filter) {
      case 'yesterday':    list = list.filter(t => t.has_activity_yesterday); break;
      case 'no_yesterday': list = list.filter(t => !t.has_activity_yesterday); break;
      case 'week':         list = list.filter(t => t.has_activity_week); break;
      case 'no_week':      list = list.filter(t => !t.has_activity_week); break;
      case 'no_month':     list = list.filter(t => !t.has_activity_month); break;
    }

    const cmp = {
      name:          (a, b) => (a.name || '').localeCompare(b.name || ''),
      last_activity: (a, b) => (a.days_since_activity ?? 9999) - (b.days_since_activity ?? 9999),
      yesterday:     (a, b) => (b.yesterday_total - a.yesterday_total),
      week:          (a, b) => (b.week_total - a.week_total),
      month:         (a, b) => (b.month_total - a.month_total),
      period:        (a, b) => (b.period_total - a.period_total),
    }[sortBy] || ((a, b) => 0);

    const sorted = [...list].sort(cmp);
    if (sortDir === 'ASC') sorted.reverse();
    return sorted;
  }, [stats, filter, planId, q, sortBy, sortDir]);

  if (loading) return <div className="cv2-page"><div className="cv2-loading"><i className="fas fa-spinner fa-spin" /> Cargando dashboard…</div></div>;
  if (error)   return <div className="cv2-page"><div className="cv2-error">Error: {error}</div></div>;
  if (!stats)  return null;

  const summary = stats.summary;
  const activePct = summary.tenants_total > 0
    ? Math.round((summary.tenants_with_activity_yesterday / summary.tenants_total) * 100)
    : 0;

  return (
    <div className="cv2-page cv2-dashboard">
      <div className="cv2-dashboard__header">
        <div>
          <h2 className="cv2-toolbar__title">
            <i className="fas fa-chart-line" /> Dashboard ejecutivo
          </h2>
          <p className="cv2-toolbar__subtitle">
            Actividad del día anterior y últimos {months} meses · período {stats.meta.period_from} → {stats.meta.period_to}
          </p>
        </div>
        <div className="cv2-dashboard__header-actions">
          <a href="/settings/clients_v2?active=true" className="cv2-btn cv2-btn--ghost">
            <i className="fas fa-list" /> Ver listado
          </a>
          <select className="cv2-select" value={months} onChange={e => setMonths(Number(e.target.value))}>
            <option value={3}>3 meses</option>
            <option value={6}>6 meses</option>
            <option value={12}>12 meses</option>
          </select>
        </div>
      </div>

      {/* SUMMARY CARDS */}
      <div className="cv2-dash-summary">
        <SummaryBig
          label="Clientes activos AYER"
          big={`${summary.tenants_with_activity_yesterday}`}
          context={`de ${summary.tenants_total} (${activePct}%)`}
          color="#22c55e"
          icon="fa-check-circle"
        />
        <SummaryBig
          label="Sin actividad AYER"
          big={`${summary.tenants_without_activity_yesterday}`}
          context={`de ${summary.tenants_total}`}
          color="#ef4444"
          icon="fa-times-circle"
        />
        <SummaryBig
          label="Surveys ayer"
          big={summary.surveys_yesterday.toLocaleString()}
          context={`${summary.surveys_30d.toLocaleString()} en 30 días`}
          color={COLORS.surveys_created}
          icon="fa-clipboard-list"
        />
        <SummaryBig
          label="Tasks ayer"
          big={summary.tasks_yesterday.toLocaleString()}
          context={`${summary.tasks_30d.toLocaleString()} en 30 días`}
          color={COLORS.tasks_created}
          icon="fa-tasks"
        />
      </div>

      {/* RANKING CHART (X = cliente, Y = registros, ordenado mayor->menor) */}
      <div className="cv2-card">
        <div className="cv2-chart-head">
          <h5>Ranking de clientes por actividad</h5>
          <div className="cv2-range-controls">
            <div className="cv2-range-tabs">
              {[
                { v: 'yesterday', l: 'Ayer'        },
                { v: 'week',      l: '7 días'      },
                { v: 'month',     l: '30 días'     },
                { v: 'quarter',   l: 'Trimestre'   },
                { v: 'semester',  l: 'Semestre'    },
              ].map(p => (
                <button
                  key={p.v}
                  className={`cv2-range-btn ${(!chartFrom || !chartTo) && chartPeriod === p.v ? 'cv2-range-btn--active' : ''}`}
                  onClick={() => pickPreset(p.v)}
                >{p.l}</button>
              ))}
            </div>
            <div className={`cv2-daterange ${chartFrom && chartTo ? 'cv2-daterange--active' : ''}`}>
              <input type="date" value={chartFrom} max={chartTo || undefined}
                     onChange={e => setChartFrom(e.target.value)} />
              <span>→</span>
              <input type="date" value={chartTo} min={chartFrom || undefined}
                     onChange={e => setChartTo(e.target.value)} />
            </div>
          </div>
        </div>
        <p className="cv2-stats__period">
          {stats.meta.chart_from} → {stats.meta.chart_to}
        </p>
        <TenantsRankingChart ranking={stats.chart_ranking} onClickTenant={id => setSelectedClientId(id)} />
      </div>

      {/* FILTERS */}
      <div className="cv2-card cv2-filters">
        <div className="cv2-filter-chips">
          {FILTERS.map(f => {
            const isActive = filter === f.value;
            return (
              <button
                key={f.value}
                className={`cv2-filter-chip ${isActive ? 'cv2-filter-chip--active' : ''}`}
                onClick={() => setFilter(f.value)}
                style={isActive && f.color ? { borderColor: f.color, color: f.color } : undefined}
              >
                <i className={`fas ${f.icon}`} /> {f.label}
              </button>
            );
          })}
        </div>
        <div className="cv2-filter-row">
          <select className="cv2-select" value={planId} onChange={e => setPlanId(e.target.value)}>
            <option value="">Todos los planes</option>
            {plans.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
          <div className="cv2-search">
            <i className="fas fa-search" />
            <input
              type="text"
              placeholder="Buscar cliente…"
              value={q}
              onChange={e => setQ(e.target.value)}
            />
          </div>
          <div className="cv2-sort">
            <span className="cv2-sort__label">Ordenar:</span>
            <select className="cv2-select" value={sortBy} onChange={e => setSortBy(e.target.value)}>
              {SORT_OPTIONS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
            <button
              className="cv2-btn cv2-btn--ghost"
              onClick={() => setSortDir(d => d === 'DESC' ? 'ASC' : 'DESC')}
              title={`Orden ${sortDir === 'DESC' ? 'descendente' : 'ascendente'}`}
            >
              <i className={`fas fa-sort-${sortDir === 'DESC' ? 'down' : 'up'}`} />
            </button>
          </div>
        </div>
        <div className="cv2-filter-summary">
          Mostrando <strong>{filteredTenants.length}</strong> de {stats.by_tenant.length} clientes
        </div>
      </div>

      {/* TENANTS TABLE */}
      <div className="cv2-table-wrap">
        <table className="cv2-table cv2-dash-table">
          <thead>
            <tr>
              <th style={{ width: 40 }}></th>
              <th>Cliente</th>
              <th style={{ width: 110 }}>Plan</th>
              <th style={{ width: 110 }} className="cv2-td--num">Ayer</th>
              <th style={{ width: 110 }} className="cv2-td--num">7d</th>
              <th style={{ width: 110 }} className="cv2-td--num">30d</th>
              <th style={{ width: 110 }} className="cv2-td--num">{months}m</th>
              <th style={{ width: 140 }}>Última actividad</th>
              <th style={{ width: 150 }}></th>
            </tr>
          </thead>
          <tbody>
            {filteredTenants.length === 0 && (
              <tr><td colSpan={9} className="cv2-empty">
                <i className="far fa-folder-open" />
                <p>Sin clientes que coincidan con los filtros</p>
              </td></tr>
            )}
            {filteredTenants.map(t => (
              <tr key={t.tenant_id}>
                <td>
                  {t.logo ? (
                    <img src={t.logo} alt="" className="cv2-logo" />
                  ) : (
                    <span className="cv2-logo cv2-logo--placeholder"><i className="fas fa-building" /></span>
                  )}
                </td>
                <td className="cv2-cell-primary">{t.name}</td>
                <td>
                  {t.plan ? (
                    <span className="cv2-chip cv2-chip--plan">{t.plan.nick_name || t.plan.name}</span>
                  ) : '—'}
                </td>
                <td className="cv2-td--num">
                  <ActivityBadge value={t.yesterday_total} hasActivity={t.has_activity_yesterday} />
                </td>
                <td className="cv2-td--num">
                  <ActivityBadge value={t.week_total} hasActivity={t.has_activity_week} />
                </td>
                <td className="cv2-td--num">
                  <ActivityBadge value={t.month_total} hasActivity={t.has_activity_month} />
                </td>
                <td className="cv2-td--num">{t.period_total.toLocaleString()}</td>
                <td>
                  <LastActivity date={t.last_activity_date} days={t.days_since_activity} />
                </td>
                <td className="cv2-td--actions">
                  <button
                    className="cv2-btn cv2-btn--ghost"
                    onClick={() => setSelectedClientId(t.tenant_id)}
                  >
                    <i className="fas fa-chart-bar" /> Ver estadísticas
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {selectedClientId && (
        <ClientShow clientId={selectedClientId} onClose={() => setSelectedClientId(null)} />
      )}
    </div>
  );
}

function SummaryBig({ label, big, context, color, icon }) {
  return (
    <div className="cv2-dash-summary__card" style={{ borderTopColor: color }}>
      <div className="cv2-dash-summary__icon" style={{ color }}>
        <i className={`fas ${icon}`} />
      </div>
      <div className="cv2-dash-summary__big" style={{ color }}>{big}</div>
      <div className="cv2-dash-summary__label">{label}</div>
      <div className="cv2-dash-summary__context">{context}</div>
    </div>
  );
}

function ActivityBadge({ value, hasActivity }) {
  if (!hasActivity || !value) {
    return <span className="cv2-act-badge cv2-act-badge--zero">0</span>;
  }
  return <span className="cv2-act-badge cv2-act-badge--has">{value.toLocaleString()}</span>;
}

function LastActivity({ date, days }) {
  if (!date) return <span className="cv2-muted">Nunca</span>;
  const cls = days === 0 ? 'green' : days <= 1 ? 'green' : days <= 7 ? 'yellow' : days <= 30 ? 'orange' : 'red';
  const label = days === 0 ? 'Hoy' : days === 1 ? 'Ayer' : `Hace ${days}d`;
  return (
    <div>
      <span className={`cv2-chip cv2-chip--state cv2-chip--${cls}`}>{label}</span>
      <div className="cv2-cell-secondary">{date}</div>
    </div>
  );
}

// Ranking de tenants: barras verticales con label vertical en X (clientes)
// y conteo de registros en Y. Ya viene ordenado DESC del backend.
function TenantsRankingChart({ ranking, onClickTenant }) {
  if (!ranking?.length) {
    return <div className="cv2-empty-state"><p>Sin clientes para mostrar</p></div>;
  }

  // Solo clientes con MÁS de 1 registro (descarta 0 y 1 — ruido visual).
  const visible = ranking.filter(r => r.total > 1);
  if (!visible.length) {
    return <div className="cv2-empty-state"><p>Ningún cliente tiene más de 1 registro en este período</p></div>;
  }
  const H = 340;
  const PAD = { top: 16, right: 16, bottom: 110, left: 50 };
  const barSlot = 36; // ancho por cliente
  const W = Math.max(800, PAD.left + PAD.right + visible.length * barSlot);
  const innerH = H - PAD.top - PAD.bottom;
  const maxY = Math.max(1, ...visible.map(r => r.total));
  const yTicks = [0, 0.25, 0.5, 0.75, 1].map(p => Math.round(maxY * p));

  // Color gradient: top = azul fuerte, bajo = rojo claro
  const colorAt = (i) => {
    if (visible.length === 1) return '#3b82f6';
    const t = i / (visible.length - 1);  // 0..1
    if (t < 0.33) return '#22c55e';  // top tier — verde
    if (t < 0.66) return '#f59e0b';  // medio — ambar
    return '#ef4444';                // cola — rojo
  };

  return (
    <div className="cv2-ranking-scroll">
      <svg width={W} height={H} preserveAspectRatio="xMidYMid meet">
        {yTicks.map((tv, i) => {
          const y = PAD.top + innerH - (tv / maxY) * innerH;
          return (
            <g key={i}>
              <line x1={PAD.left} y1={y} x2={W - PAD.right} y2={y} stroke="#eef0f3" strokeWidth="1" />
              <text x={PAD.left - 6} y={y + 4} textAnchor="end" fontSize="10" fill="#94a3b8">{tv.toLocaleString()}</text>
            </g>
          );
        })}
        {visible.map((r, i) => {
          const v = r.total;
          const h = (v / maxY) * innerH;
          const x = PAD.left + i * barSlot + 4;
          const y = PAD.top + innerH - h;
          const w = barSlot - 8;
          const color = colorAt(i);
          const labelX = x + w / 2;
          const labelY = PAD.top + innerH + 12;
          // Truncar nombre si es muy largo
          const dispName = r.name.length > 24 ? r.name.slice(0, 22) + '…' : r.name;
          return (
            <g key={r.tenant_id} style={{ cursor: 'pointer' }} onClick={() => onClickTenant(r.tenant_id)}>
              <rect x={x} y={y} width={w} height={h} fill={color} rx="2" opacity={v > 0 ? 1 : 0.25}>
                <title>{`${r.name}: ${v.toLocaleString()} registros`}</title>
              </rect>
              {/* Numero arriba de la barra cuando hay valor */}
              {v > 0 && h > 16 && (
                <text x={labelX} y={y - 4} textAnchor="middle" fontSize="10" fontWeight="600" fill="#1e293b">
                  {v.toLocaleString()}
                </text>
              )}
              {/* Label rotado 90deg para que quepan muchos */}
              <text
                x={labelX} y={labelY}
                fontSize="10" fill="#475569"
                transform={`rotate(60, ${labelX}, ${labelY})`}
              >
                {dispName}
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}

function _MonthlyBarChartUnused({ data }) {
  const W = 800;
  const H = 260;
  const PAD = { top: 16, right: 16, bottom: 36, left: 44 };
  const innerW = W - PAD.left - PAD.right;
  const innerH = H - PAD.top - PAD.bottom;

  if (!data?.length) return <div className="cv2-empty-state"><p>Sin datos</p></div>;

  const series = [
    { key: 'surveys_created', color: COLORS.surveys_created, label: 'Surveys creados' },
    { key: 'surveys_edited',  color: COLORS.surveys_edited,  label: 'Surveys editados' },
    { key: 'tasks_created',   color: COLORS.tasks_created,   label: 'Tasks creadas' },
    { key: 'tasks_edited',    color: COLORS.tasks_edited,    label: 'Tasks editadas' },
  ];

  const maxY = Math.max(1, ...data.flatMap(d => series.map(s => d[s.key] || 0)));
  const groupW = innerW / data.length;
  const barW = (groupW - 12) / series.length;
  const yTicks = [0, 0.25, 0.5, 0.75, 1].map(p => Math.round(maxY * p));

  return (
    <>
      <svg width="100%" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="xMidYMid meet">
        {yTicks.map((tv, i) => {
          const y = PAD.top + innerH - (tv / maxY) * innerH;
          return (
            <g key={i}>
              <line x1={PAD.left} y1={y} x2={W - PAD.right} y2={y} stroke="#eef0f3" strokeWidth="1" />
              <text x={PAD.left - 6} y={y + 4} textAnchor="end" fontSize="10" fill="#94a3b8">{tv.toLocaleString()}</text>
            </g>
          );
        })}
        {data.map((m, idx) => {
          const x0 = PAD.left + idx * groupW + 6;
          return (
            <g key={m.month}>
              {series.map((s, si) => {
                const v = m[s.key] || 0;
                const h = (v / maxY) * innerH;
                const x = x0 + si * barW;
                const y = PAD.top + innerH - h;
                return (
                  <rect key={s.key} x={x} y={y} width={barW - 1} height={h} fill={s.color} rx="2">
                    <title>{`${m.label} · ${s.label}: ${v.toLocaleString()}`}</title>
                  </rect>
                );
              })}
              <text
                x={x0 + (series.length * barW) / 2 - 4}
                y={H - 18}
                textAnchor="middle"
                fontSize="11"
                fill="#475569"
                fontWeight="600"
              >{m.label}</text>
              <text
                x={x0 + (series.length * barW) / 2 - 4}
                y={H - 5}
                textAnchor="middle"
                fontSize="9"
                fill="#94a3b8"
              >{m.active_tenants} clientes</text>
            </g>
          );
        })}
      </svg>
      <div className="cv2-legend">
        {series.map(s => (
          <span key={s.key} className="cv2-legend__item">
            <i style={{ background: s.color }} /> {s.label}
          </span>
        ))}
      </div>
    </>
  );
}
